import { FC, Fragment, useEffect, useMemo, useState } from 'react'
import { FeatureFlag, NumberFeatureFlag, useFeatureFlag, useFeatureFlagContext, useNumberFeatureFlag } from 'utils/featureFlags'
import { UPLOAD_CONCURRENCY, setUploadConcurrency } from 'components/common/FileAPI/uploadPool'

import { ActionPopup } from 'components/common/Modals/ActionPopup/ActionPopup'
import { BKBNThemeWrapper } from 'components/common/BKBNThemeWrapper'
import { ChatWidget } from 'components/common/ChatWidget'
import { MaintenancePage } from 'components/pages/Maintenance'
import { Path, isEmbedRoute } from 'constants/router'
import PathLogger from '../PathLogger/PathLogger'
import { RouteSwitch } from '../RouteSwitch'
import { Snackbar } from 'components/common/Snackbar'
import TopBar from 'components/common/TopBar/TopBar'
import { Userpilot } from 'userpilot'
import { hotjar } from 'react-hotjar'
import { useBecka } from 'utils/becka'
import { initSatisMeter } from 'utils/satismeter'
import { shouldShowChatWidget } from './shouldShowChatWidget'
import { useWorkspaceInvitationTracking } from 'utils/analytics'
import { useAppHeaderSwitch } from '../AppHeaderSwitch'
import { useAuth0 } from '../../../utils/auth'
import { useLocation } from 'react-router'
import { useUserData } from 'components/contexts/UserDataContext'
import { useFlagUserId } from 'utils/featureFlags/useFlagUserId'

export const AppController: FC = () => {

  const { baseUserData } = useUserData()
  const { routeHasNoHeader } = useAppHeaderSwitch()
  const { pathname } = useLocation()

  // Inside the Propstack partner iframe, suppress third-party support/tracking widgets
  // (Becka/Salesforce messaging, SatisMeter, Hotjar, Userpilot, ChatWidget) while keeping
  // Backbone's own funnel/product analytics. See ISSUE-2310.
  const isEmbed = isEmbedRoute(pathname)

  const isSiteInMaintenance = useFeatureFlag(FeatureFlag.IS_SITE_IN_MAINTANANCE)
  const allowChatWidget = useFeatureFlag(FeatureFlag.ALLOW_EXTERNAL_COMMUNICATION_PF)
  const uploadConcurrency = useNumberFeatureFlag(NumberFeatureFlag.UPLOAD_CONCURRENCY, UPLOAD_CONCURRENCY)
  const { isAuthenticated, user, roles, getTokenSilently } = useAuth0()
  const updateFFContext = useFeatureFlagContext()

  const [token, setToken] = useState<string>()

  const showHeader = useMemo(() => {
    if (pathname === Path.INDEX && !isAuthenticated) return false
    return !routeHasNoHeader
  }, [isAuthenticated, pathname, routeHasNoHeader])

  useEffect(() => {
    (async () => {
      const jwt = await getTokenSilently()
      if (!jwt) return
      setToken(jwt)
    })()
    // Only trigger on load
  }, [getTokenSilently])

  // The upload pool is a module singleton, so the flag is pushed to it rather than read
  // there — that keeps the LaunchDarkly graph out of the FileAPI modules and their tests.
  useEffect(() => {
    setUploadConcurrency(uploadConcurrency)
  }, [uploadConcurrency])

  // Initialize Becka if allowed (suppressed in embed)
  useBecka(token, baseUserData, isEmbed)

  // Initialize SatisMeter if authenticated (suppressed in embed)
  useEffect(() => {
    if (isAuthenticated && !isEmbed) {
      const userId = baseUserData?.id

      const traits = {
        name: baseUserData?.name,
        email: baseUserData?.email,
        userRole: roles.isClient ? 'client' : roles.isCreative ? 'creative' : 'admin',
        language: baseUserData?.language,
        country: baseUserData?.country,
      }

      if (userId) {
        initSatisMeter(userId, traits)
      }
    }
  }, [isAuthenticated, baseUserData, user, roles, isEmbed])

  // Flag targeting identity. Deliberately NOT gated on `isEmbed`, unlike Becka,
  // SatisMeter, Hotjar and the chat widget above: those are analytics, which
  // ISSUE-2310 suppresses inside a partner iframe, whereas LaunchDarkly is
  // application configuration the app cannot serve correctly without — the embed
  // routes are themselves flag-gated. User-targeted rules therefore have to
  // evaluate here too, so re-adding a gate would break the embed rather than
  // harden it. Cleared on sign-out so a session that never reloads stops
  // evaluating flags as the previous user.
  const flagUser = useFlagUserId()
  useEffect(() => {
    updateFFContext({ userId: flagUser })
  }, [flagUser, updateFFContext])

  // Update SPA state for hotjar and trigger Userpilot reload when path changes
  // (skipped in embed — neither tool is initialized there)
  useEffect(() => {
    if (isEmbed) return

    const currentPath = pathname

    // Check if Hotjar has been initialized before calling its methods
    if (hotjar.initialized()) {
      hotjar.stateChange(currentPath)
    }

    if (isAuthenticated) {
      Userpilot.reload()
    }
  }, [isAuthenticated, pathname, isEmbed])

  useWorkspaceInvitationTracking()

  if (isSiteInMaintenance) return <MaintenancePage />

  return (
    <div>
      <ActionPopup isGlobal={true} />

      <Snackbar />

      <PathLogger />

      <Fragment>
        <BKBNThemeWrapper>
          {showHeader &&
            <TopBar />
          }
          <RouteSwitch />

          {shouldShowChatWidget({ isAuthenticated, roles, allowChatWidget, isEmbed, hasPlatformAccount: !!baseUserData?.id }) &&
            <ChatWidget />
          }
        </BKBNThemeWrapper>
      </Fragment>

    </div>
  )
}
